import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import FormElements, {
  FORMELEMENTS_ACCEPT_BUTTON,
  FORMELEMENTS_CHECKBOXES,
  FORMELEMENTS_DATEPICKER,
  FORMELEMENTS_DROPDOWN,
  FORMELEMENTS_LABEL,
  FORMELEMENTS_LINEBREAK,
  FORMELEMENTS_NUMBERINPUT,
  FORMELEMENTS_PARAGRAPH,
  FORMELEMENTS_RADIOBUTTONS,
  FORMELEMENTS_SIGNATURE,
  FORMELEMENTS_TEXTAREA,
  FORMELEMENTS_TEXTINPUT,
  FORMELEMENTS_TIMEPICKER,
  ACCEPT_BUTTON_ACCEPTED,
  FORMELEMENTS_MOBILE_SPLIT_NUMBER
} from './FormElements';
import { List, ListGroup } from 'framework7-react';
import { formWidgetHtmlId, getAbsPlaceStyle, updateTabIndex } from './utils';
import FormInput from './FormInput';
import Delayed from '../helpers/Delayed';
import { confirmDialog } from '../helpers/dialogUtils';

export default class FormGenerator extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      currentMountedPage: -1
    };
  }

  componentDidMount() {
    !this.props.quickForm && updateTabIndex(this.props.formStructure);

    if (this.state.currentMountedPage !== this.props.pageNumber) {
      this.setState({
        currentMountedPage: this.props.pageNumber
      });
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    updateTabIndex(this.props.formStructure, this.props.quickForm);

    if (this.state.currentMountedPage !== this.props.pageNumber) {
      this.setState({
        currentMountedPage: this.props.pageNumber
      });
    }
  }

  onAcceptButtonClick = item => {
    if (this._getItemValue(item) !== ACCEPT_BUTTON_ACCEPTED) {
      confirmDialog(item.label, '', () => {
        this.handleChange(item, ACCEPT_BUTTON_ACCEPTED);
      });
    }
  };

  _getItemValue(item) {
    return this.props.formResult.get(item.id);
  }

  _prepareItemValue(item) {
    const value = this._getItemValue(item);
    return value !== undefined ? value : '';
  }

  // noinspection JSUnusedLocalSymbols
  handleBlur = (id, event) => {};

  handleClick = item => {
    const element = document.getElementById(formWidgetHtmlId(item));
    element && element.focus();

    if (item.element === FORMELEMENTS_ACCEPT_BUTTON) {
      this.onAcceptButtonClick(item);
    }

    this.props.onItemClick && this.props.onItemClick(item);
  };

  handleFocus = item => {
    this.props.onItemFocus && this.props.onItemFocus(item);
  };

  handleChange = (item, value, data, forceUpdate) => {
    this.props.onAnyChange(item, value, data);
    forceUpdate && this.forceUpdate();
  };

  delayElement(element, index, id) {
    const delayItemRender =
      this.state.currentMountedPage === this.props.pageNumber
        ? 0
        : index < 5
        ? 10
        : 100 + 10 * index;

    return this.props.allowDelayElements ? (
      <Delayed key={'delay_' + id} waitBeforeShow={delayItemRender}>
        {element}
      </Delayed>
    ) : (
      element
    );
  }

  getInputElement(item, hasCustomTabIndex) {
    const {
      scaleFactor,
      activeElementIndex,
      showActiveAnimation,
      editBarMode,
      absolutePlaceMode,
      formErrors,
      showRequiredErrors,
      readOnly,
      ios,
      desktop,
      quickForm,
      dpiModifier
    } = this.props;

    return this.delayElement(
      <FormInput
        showPopupsInPortal={this.props.showPopupsInPortal}
        hasCustomTabIndex={hasCustomTabIndex}
        key={item.id}
        isFieldLinked={Boolean(item.valueInheritance)}
        scaleFactor={scaleFactor}
        active={
          item.elementIndex !== undefined &&
          item.elementIndex === activeElementIndex
        }
        showActiveAnimation={showActiveAnimation}
        editBarMode={editBarMode}
        absolutePlaceMode={absolutePlaceMode}
        showRequiredErrors={showRequiredErrors}
        readOnly={readOnly}
        handleChange={this.handleChange}
        handleClick={this.handleClick}
        handleFocus={this.handleFocus}
        handleBlur={this.handleBlur}
        item={item}
        error={formErrors && formErrors.get(item.id)}
        ios={ios}
        value={this._prepareItemValue(item)}
        desktop={desktop}
        quickForm={quickForm}
        dpiModifier={dpiModifier}
        isQualified={item.isQesWidget}
        requestedDocuments={this.props.requestedDocuments}
      />,
      item.elementIndex,
      item.id
    );
  }

  getSimpleElement(item) {
    const Element = FormElements[item.element];
    // noinspection CheckTagEmptyBody
    return Element ? (
      this.delayElement(
        Element({
          key: `form_${item.id}`,
          data: item,
          style: {
            ...(this.props.absolutePlaceMode &&
              getAbsPlaceStyle(item, this.props.scaleFactor, item.label)),
            ...(item.hidden && { display: 'none' })
          }
        }),
        item.elementIndex,
        item.id
      )
    ) : (
      <div></div>
    );
  }

  render() {
    const formStructure = this.props.formStructure;
    const pageNumber = this.props.pageNumber ? this.props.pageNumber : '';
    const lists = [];

    let itemsGroups = [];
    let items = [];
    let itemsGroupsKey = 0;

    const pushItemsToGroup = () => {
      items.length &&
        itemsGroups.push(
          <ListGroup key={'group_' + itemsGroupsKey++}>{items}</ListGroup>
        );
      items = [];
    };
    const hasCustomTabIndex = formStructure.some(
      item => item.tabIndex !== undefined
    );

    formStructure.forEach(item => {
      switch (item.element) {
        case FORMELEMENTS_TEXTINPUT:
        case FORMELEMENTS_MOBILE_SPLIT_NUMBER:
        case FORMELEMENTS_NUMBERINPUT:
        case FORMELEMENTS_TEXTAREA:
        case FORMELEMENTS_DROPDOWN:
        case FORMELEMENTS_DATEPICKER:
        case FORMELEMENTS_TIMEPICKER:
        case FORMELEMENTS_SIGNATURE:
        case FORMELEMENTS_ACCEPT_BUTTON:
          items.push(this.getInputElement(item, hasCustomTabIndex));
          break;
        case FORMELEMENTS_RADIOBUTTONS:
        case FORMELEMENTS_CHECKBOXES:
          pushItemsToGroup();
          itemsGroups.push([this.getInputElement(item, hasCustomTabIndex)]);
          break;
        case FORMELEMENTS_LABEL:
        case FORMELEMENTS_PARAGRAPH:
        case FORMELEMENTS_LINEBREAK:
          pushItemsToGroup();
          itemsGroups.length && lists.push(itemsGroups);
          lists.push([this.getInputElement(item)]);
          itemsGroups = [];
          break;
        default:
          items.push(this.getSimpleElement(item));
      }
    });

    pushItemsToGroup();
    itemsGroups.length && lists.push(itemsGroups);

    return (
      <Fragment>
        <div className={'react-form-builder-form '}>
          <div
            id={'formBuilder_' + pageNumber}
            className={'formBuilder' + (this.props.desktop ? ' desktop' : '')}
            onSubmit={e => {
              e.preventDefault();
            }}
          >
            <List id={'form_elements'}>
              {lists.map((itemsGroups, index) => (
                <Fragment key={'list_' + index}>
                  {itemsGroups.map(items => items)}
                </Fragment>
              ))}
            </List>
          </div>
        </div>
      </Fragment>
    );
  }
}

FormGenerator.propTypes = {
  showPopupsInPortal: PropTypes.bool,
  formStructure: PropTypes.array.isRequired,
  formResult: PropTypes.instanceOf(Map).isRequired,
  scaleFactor: PropTypes.object,
  formErrors: PropTypes.object,
  desktop: PropTypes.bool,
  absolutePlaceMode: PropTypes.bool,
  editBarMode: PropTypes.bool,
  editBarModeId: PropTypes.string,
  showActiveAnimation: PropTypes.bool,
  activeElementIndex: PropTypes.number,
  pageNumber: PropTypes.number,
  showRequiredErrors: PropTypes.bool,
  readOnly: PropTypes.bool,
  ios: PropTypes.bool,
  quickForm: PropTypes.bool,
  allowDelayElements: PropTypes.bool,
  onAnyChange: PropTypes.func,
  onItemClick: PropTypes.func,
  onItemFocus: PropTypes.func,
  dpiModifier: PropTypes.number,
  documentId: PropTypes.string,
  documentName: PropTypes.string,
  requestedDocuments: PropTypes.array
};
